import { useState } from "react";

import { Drawer, type DrawerBaseProps } from "@/components/Drawer/Drawer";
import { Button } from "@/design-system/Button";
import { FormField } from "@/design-system/FormField";
import { Select } from "@/design-system/Select";
import { Stack } from "@/design-system/Stack";
import { usePortfolioAnalyticsProjectFilterDraft } from "@/modules/analytics/PortfolioAnalyticsProjectFilterFields";
import type { BookingAnalyticsOrigin, BookingAnalyticsStatus } from "@/queries/analytics/booking-reporting-types";
import { useTranslation } from "@/translations";

import type { BookingAnalyticsTableTab } from "./BookingAnalyticsContent";

/** Asset, origin, and status values applied to booking analytics. */
export interface BookingAnalyticsFilterValues {
  assetId: string;
  origin: BookingAnalyticsOrigin;
  status: BookingAnalyticsStatus;
}

type BookingAnalyticsFilterDrawerProps = DrawerBaseProps & {
  tableTab: BookingAnalyticsTableTab;
  filter: BookingAnalyticsFilterValues;
  assets: {
    id: string;
    name: string;
  }[];
  showOriginFilter: boolean;
  onUpdate: (filter: BookingAnalyticsFilterValues) => void;
};

type FilterOption<T> = {
  value: T;
  label: string;
};

/** Renders filters for the bookings or assets context selected by `tableTab`. */
export function BookingAnalyticsFilterDrawer({
  isOpened,
  onOpenChange,
  tableTab,
  filter,
  assets,
  showOriginFilter,
  onUpdate,
}: BookingAnalyticsFilterDrawerProps): React.ReactNode {
  const { t } = useTranslation();

  return (
    <Drawer.Root
      title={t(
        tableTab === "assets"
          ? "page.analytics-bookings.filters.assets-title"
          : "page.analytics-bookings.filters.title",
      )}
      isOpened={isOpened}
      onOpenChange={onOpenChange}
      data-testid="booking-analytics-filter-drawer"
    >
      {isOpened && (
        <FilterForm filter={filter} assets={assets} showOriginFilter={showOriginFilter} onUpdate={onUpdate} />
      )}
    </Drawer.Root>
  );
}

function FilterForm({
  filter,
  assets,
  showOriginFilter,
  onUpdate,
}: Pick<BookingAnalyticsFilterDrawerProps, "filter" | "assets" | "showOriginFilter" | "onUpdate">): React.ReactNode {
  const { t } = useTranslation();
  const [draftFilter, setDraftFilter] = useState(filter);
  const portfolioProjectFilter = usePortfolioAnalyticsProjectFilterDraft();
  const assetOptions: FilterOption<string>[] = [
    { value: "all", label: t("page.analytics-bookings.filters.asset.all") },
    ...assets.map((asset) => ({ value: asset.id, label: asset.name })),
  ];
  const originOptions: FilterOption<BookingAnalyticsOrigin>[] = [
    { value: "all", label: t("page.analytics-bookings.filters.origin.all") },
    { value: "projectOnly", label: t("page.analytics-bookings.filters.origin.project-only") },
    { value: "projectConnection", label: t("page.analytics-bookings.filters.origin.project-connection") },
  ];
  const statusOptions: FilterOption<BookingAnalyticsStatus>[] = [
    { value: "all", label: t("page.analytics-bookings.filters.status.all") },
    { value: "upcoming", label: t("page.analytics-bookings.filters.status.upcoming") },
    { value: "completed", label: t("page.analytics-bookings.filters.status.completed") },
    { value: "cancelled", label: t("page.analytics-bookings.filters.status.cancelled") },
  ];
  const selectedAsset = assetOptions.find((option) => option.value === draftFilter.assetId) ?? assetOptions[0];
  const selectedOrigin = originOptions.find((option) => option.value === draftFilter.origin)!;
  const selectedStatus = statusOptions.find((option) => option.value === draftFilter.status)!;

  const handleClear = () => {
    const clearedFilter: BookingAnalyticsFilterValues = { assetId: "all", origin: "all", status: "all" };
    setDraftFilter(clearedFilter);
    onUpdate(clearedFilter);
    portfolioProjectFilter.clear();
  };

  return (
    <>
      <Drawer.Body>
        <Stack gap="md">
          {portfolioProjectFilter.fields}
          {assets.length > 0 && (
            <FormField label={t("page.analytics-bookings.filters.asset.label")}>
              <Select
                items={assetOptions}
                selected={selectedAsset}
                onChange={(option) => setDraftFilter((current) => ({ ...current, assetId: option.value }))}
                renderOption={(option) => option.label}
                keySelector={(option) => option.value}
              />
            </FormField>
          )}
          {showOriginFilter && (
            <FormField label={t("page.analytics-bookings.filters.origin.label")}>
              <Select
                items={originOptions}
                selected={selectedOrigin}
                onChange={(option) => setDraftFilter((current) => ({ ...current, origin: option.value }))}
                renderOption={(option) => option.label}
                keySelector={(option) => option.value}
              />
            </FormField>
          )}
          <FormField label={t("page.analytics-bookings.filters.status.label")}>
            <Select
              items={statusOptions}
              selected={selectedStatus}
              onChange={(option) => setDraftFilter((current) => ({ ...current, status: option.value }))}
              renderOption={(option) => option.label}
              keySelector={(option) => option.value}
            />
          </FormField>
        </Stack>
      </Drawer.Body>
      <Drawer.Actions>
        <Drawer.Close>
          <Button variant="subtle" onClick={handleClear}>
            {t("common.action.clear")}
          </Button>
        </Drawer.Close>
        <Drawer.Close>
          <Button
            onClick={() => {
              onUpdate({ ...draftFilter, assetId: selectedAsset.value });
              portfolioProjectFilter.apply();
            }}
          >
            {t("common.action.apply")}
          </Button>
        </Drawer.Close>
      </Drawer.Actions>
    </>
  );
}
